import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Channel as ChannelType, ChannelFilters, ChannelSort, DefaultGenerics, TokenOrProvider } from "stream-chat";
import { Channel, ChannelHeader, ChannelList, Chat, DefaultStreamChatGenerics, MessageInput, MessageList, Thread, Window, useCreateChatClient } from 'stream-chat-react';
import { useToast } from "../../../Hooks/useToast";
import { useUser } from "../../../Hooks/useUser";
import { useCreateChatUserMutation } from "../../../Redux/api/chatApi";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { KaziButton } from "../../../Shared Components/Kazi/KaziButton";
import { AppSettings } from "../../../Utils/Settings";
import { formatUserName } from "../../../Utils/String";
import { setTalentSearchResult } from "../../../Redux/slices/TalentSearchSlice";
import ComposeMessageModal from "./Compose/ComposeMessageModal";
import { useSearchParams } from "react-router-dom";
import { useMutationObserver } from "../../../Hooks/useMustationObserver";

const apiKey = AppSettings.StreamChatApiKey;

interface IProps {
    token: TokenOrProvider;
}
const StreamChatContainer = ({token}:IProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { locator } = useUser();
    const chatUsers = useAppSelector( state => state.Global.chatUsers);
    const [ createChatClient ] = useCreateChatUserMutation();
    const { showToast } = useToast(); 
    const [ searchParams ] = useSearchParams();
    /* ******************** Variables ******************** */
    const client = useCreateChatClient({
        apiKey,
        tokenOrProvider: token,
        userData: { id: locator!}
    });
    const sort: ChannelSort<DefaultStreamChatGenerics> | undefined = { last_message_at:  -1};
    const filters: ChannelFilters<DefaultStreamChatGenerics> | undefined = { members: { $in: [locator!] } };
    const [modalOpen, setModalOpen] = useState(false);
    const [disableCompose, setDisableCompose] = useState(false);
    const [ creatingChat, setCreatingChat ] = useState(false);
    const [activeChannels, setActiveChannels]= useState<ChannelType<DefaultGenerics>[] | null>(null);
    const conversation = searchParams.get('conversation');
    const conversationName = searchParams.get('name');
    const { observe, disconnect } = useMutationObserver();
    /* ******************** Functions ******************** */
    const startBodyObserver = async () => {
        observe(document.body, 'body', handleNewChatAdded);
    }
    const disconnectBodyObserver = () => {
        disconnect('body');
    }
    const handleNewChatAdded = (node: any) => {
        if (node.className === 'str-chat__channel-preview-container') {
            // console.log('newElementadded: ', node);
            const newChat = node.querySelector(`button[aria-label*='Select Channel']`);
            if (newChat) {
                //@ts-ignore
                newChat.click();
                disconnectBodyObserver();
            }
        }
    }
    const handleComposeClick= () => {
        setModalOpen(true);
    }
    const handleCompose = async (user: any , name?: string, shouldDisableCompose: boolean = false) => {
        startBodyObserver();
        const username = name ?? formatUserName(user.first_name, user.last_name, true);
        const receiverLocator = user.locator
        const channel = client!.channel('messaging', {
            members: [locator, receiverLocator]
        })
        const createChannel = async () => {
            await channel.watch();
            dispatch(setTalentSearchResult([]));
            if (shouldDisableCompose){
                setDisableCompose(true);
            }
            setModalOpen(false);
            setCreatingChat(false);
        }
        if (chatUsers?.users?.some( c => c.id === receiverLocator)) {
            createChannel();
        } else {
            await createChatClient({
                id: receiverLocator,
                name: user?.company || username
            })
            .unwrap()
            .then( res => {
                createChannel();
            })
            .catch( err => {
                console.error("Error occured while composing message: ", err);
                showToast({
                    text: "Unable to compose message",
                    variant: 'error'
                })
            })
        }
    }
    /* ******************** Effects ******************** */
    useEffect(() =>{
        if (!creatingChat && conversation && conversationName && activeChannels) {
            const chats = activeChannels.map( c => Object.values(c.state.members).map( m => m.user_id)).flat().filter( c => c !== locator)
            // console.log('activeChannels: ', client?.activeChannels)
            // console.log('conversation: ', conversation);
            // console.log('chats: ', chats);
            if (chats.some( c => c === conversation)) {
                const selectedChat = document.querySelector(`button[aria-label*='${conversationName}']`)
                if (selectedChat) {
                    //@ts-ignore
                    selectedChat.click();
                    setCreatingChat(true);
                }
            } else {
                setCreatingChat(true);
                handleCompose({locator: conversation}, conversationName)
            }
        } 
    }, [conversation, conversationName, activeChannels])
    useEffect(() =>{
        if (client) {
            client.on((event) => {
                setActiveChannels(client?.activeChannels ? Object.values(client?.activeChannels) : null)
            })
            return client.off((event) => {
                setActiveChannels(client?.activeChannels ? Object.values(client?.activeChannels) : null)
            })
        }
    }, [client])
    /* ******************** JSX ******************** */
    if (!client) return <Box display='flex' justifyContent='center' alignItems='center' height='100%'><CircularProgress /></Box>
    return (
        <>
            <Box
                display='flex'
                width={'100%'}
                height={'100%'}
                position={'relative'}
            >
                <Chat 
                    {...{
                        client
                    }} 
                >
                    <KaziButton 
                        text="Compose message"
                        variant="contained"
                        disabled={disableCompose}
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            left: 40,
                            zIndex: 1
                        }}
                        onClick={handleComposeClick}
                    />
                    <ChannelList 
                        {...{
                            sort,
                            filters
                        }}
                    />
                     <Channel>
                        <Window>
                            <ChannelHeader />
                            <MessageList />
                            <MessageInput />
                        </Window>
                        <Thread />
                    </Channel>
                   { client && <ComposeMessageModal 
                        open={modalOpen} 
                        setOpen={setModalOpen} 
                        handleCompose={handleCompose} 
                    />}
                </Chat>
            </Box>   
        </>
    )
}

export default StreamChatContainer